import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Form,
  Input,
  Select,
  Space,
  Typography,
  Tag,
  Popconfirm,
  message,
  Badge,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { ArtifactService } from '../../services/api/artifactService';
import { CompanyService } from '../../services/api/companyService';
import { Company } from '../../types/company';
import './ArtifactTreeManager.css';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface ArtifactTree {
  id: string;
  name: string;
  description?: string;
  created_at: string;
  updated_at?: string;
  artifact_count?: number;
  company_id?: string;
  company_name?: string;
  tree_metadata?: any;
}

interface ArtifactTreeManagerProps {
  className?: string;
}

export const ArtifactTreeManager: React.FC<ArtifactTreeManagerProps> = ({ className }) => {
  const [trees, setTrees] = useState<ArtifactTree[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTree, setSelectedTree] = useState<ArtifactTree | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  useEffect(() => {
    loadTrees();
    loadCompanies();
  }, []);

  // Populate form fields when tree is selected
  useEffect(() => {
    if (selectedTree && !isCreating) {
      // Get company_id from direct field or from tree_metadata
      const companyId = selectedTree.company_id || 
                       selectedTree.tree_metadata?.company_id || 
                       undefined;
      
      form.setFieldsValue({
        name: selectedTree.name || '',
        description: selectedTree.description || '',
        company_id: companyId,
      });
    } else if (isCreating) {
      // Clear form for new tree
      form.resetFields();
    }
  }, [selectedTree, isCreating, form]);

  const loadCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const companyList = await CompanyService.getAll({ is_active: true });
      setCompanies(companyList);
    } catch (err: any) {
      console.error('Failed to load companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const loadTrees = async () => {
    setLoading(true);
    try {
      console.log('🔄 [ArtifactTreeManager] Fetching artifact trees (admin view - no company filter)');
      const response = await ArtifactService.getTrees(); // No company filter for admin view
      console.log('✅ [ArtifactTreeManager] Received artifact trees:', {
        count: response.length,
        trees: response.map(t => ({ 
          id: t.id, 
          name: t.name, 
          company_id: t.company_id,
          company_name: t.company_name, 
          artifact_count: t.artifact_count,
          tree_metadata: t.tree_metadata
        }))
      });
      if (response.length > 0) {
        const sample = response[0];
        console.log('📊 [ArtifactTreeManager] Sample tree data:', {
          id: sample.id,
          name: sample.name,
          company_id: sample.company_id,
          company_name: sample.company_name,
          tree_metadata: sample.tree_metadata,
          allKeys: Object.keys(sample)
        });
      }
      setTrees(response);
    } catch (err: any) {
      console.error('❌ [ArtifactTreeManager] Failed to load artifact trees:', err);
      message.error(err.message || 'Failed to load artifact trees');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTree = () => {
    setSelectedTree(null);
    setIsCreating(true);
    form.resetFields();
  };

  const handleSelectTree = (tree: ArtifactTree) => {
    setSelectedTree(tree);
    setIsCreating(false);
  };

  const handleDeleteTree = async (treeId: string) => {
    try {
      await ArtifactService.deleteTree(treeId);
      message.success('Artifact tree deleted successfully');
      if (selectedTree?.id === treeId) {
        setSelectedTree(null);
        setIsCreating(false);
        form.resetFields();
      }
      await loadTrees();
    } catch (err: any) {
      message.error(err.message || 'Failed to delete artifact tree');
    }
  };

  const handleSaveTree = async () => {
    try {
      const values = await form.validateFields();
      
      if (selectedTree && !isCreating) {
        // Update existing tree
        const updateData: any = {
          name: values.name?.trim(),
          description: values.description?.trim() || null,
        };
        
        // Add company_id as a direct field if provided
        if (values.company_id) {
          updateData.company_id = values.company_id;
        } else {
          // Send null to clear company_id
          updateData.company_id = null;
        }
        
        // Preserve existing tree_metadata if it exists (for other metadata fields)
        if (selectedTree.tree_metadata && typeof selectedTree.tree_metadata === 'object') {
          // Only preserve non-company_id fields
          const { company_id: _, ...otherMetadata } = selectedTree.tree_metadata;
          if (Object.keys(otherMetadata).length > 0) {
            updateData.tree_metadata = otherMetadata;
          }
        }
        
        await ArtifactService.updateTree(selectedTree.id, updateData);
        message.success('Artifact tree updated successfully');
      } else {
        // Create new tree
        const createData: any = {
          name: values.name?.trim() || '',
          description: values.description?.trim() || null,
        };
        
        if (values.company_id) {
          createData.company_id = values.company_id;
        }
        
        await ArtifactService.createTree(
          createData.name, 
          createData.description, 
          createData.company_id ? { company_id: createData.company_id } : undefined
        );
        message.success('Artifact tree created successfully');
        setIsCreating(false);
        form.resetFields();
      }
      
      await loadTrees();
    } catch (err: any) {
      console.error('Error saving artifact tree:', err);
      message.error(err.message || 'Failed to save artifact tree');
    }
  };

  const filteredTrees = trees.filter(tree => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      tree.name.toLowerCase().includes(searchLower) ||
      tree.description?.toLowerCase().includes(searchLower) ||
      tree.company_name?.toLowerCase().includes(searchLower)
    );
  });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Text strong>{text}</Text>,
      sorter: (a: ArtifactTree, b: ArtifactTree) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (text: string) => text || <Text type="secondary">No description</Text>,
      ellipsis: true,
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company',
      render: (text: string, record: ArtifactTree) => {
        // Show company_name if available, otherwise try to find company name from companies list
        if (text) return text;
        if (record.company_id) {
          const company = companies.find(c => c.id === record.company_id);
          if (company) return company.name;
        }
        // Also check tree_metadata for company_id
        if (record.tree_metadata?.company_id) {
          const company = companies.find(c => c.id === record.tree_metadata.company_id);
          if (company) return company.name;
        }
        return <Text type="secondary">N/A</Text>;
      },
      sorter: (a: ArtifactTree, b: ArtifactTree) => {
        const aName = a.company_name || companies.find(c => c.id === a.company_id)?.name || '';
        const bName = b.company_name || companies.find(c => c.id === b.company_id)?.name || '';
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Artifacts',
      dataIndex: 'artifact_count',
      key: 'artifact_count',
      render: (count: number) => (
        <Badge count={count || 0} showZero style={{ backgroundColor: '#1890ff' }} />
      ),
      sorter: (a: ArtifactTree, b: ArtifactTree) => (a.artifact_count || 0) - (b.artifact_count || 0),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => {
        if (!date) return '-';
        try {
          // Handle both string and datetime object formats
          const dateObj = typeof date === 'string' ? new Date(date) : date;
          if (isNaN(dateObj.getTime())) return '-';
          return dateObj.toLocaleDateString();
        } catch {
          return '-';
        }
      },
      sorter: (a: ArtifactTree, b: ArtifactTree) => {
        try {
          const dateA = a.created_at ? (typeof a.created_at === 'string' ? new Date(a.created_at) : a.created_at) : new Date(0);
          const dateB = b.created_at ? (typeof b.created_at === 'string' ? new Date(b.created_at) : b.created_at) : new Date(0);
          return dateA.getTime() - dateB.getTime();
        } catch {
          return 0;
        }
      },
    },
  ];

  const totalTrees = trees.length;
  const totalArtifacts = trees.reduce((sum, tree) => sum + (tree.artifact_count || 0), 0);

  return (
    <div className={`artifact-tree-manager ${className || ''}`}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Artifact Tree Manager</Title>
        <Text type="secondary">Manage artifact trees and their configurations</Text>
      </div>

      <Row gutter={16} className="artifact-tree-manager-container">
        {/* Left Pane - Tree List */}
        <Col xs={24} lg={10} xl={9} className="artifact-tree-list-panel">
          <Card
            title={
              <Space>
                <Title level={4} style={{ margin: 0 }}>Artifact Trees</Title>
                <Tag>{filteredTrees.length} trees</Tag>
              </Space>
            }
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleCreateTree}
                size="small"
              >
                Add Tree
              </Button>
            }
            className="tree-list-card"
          >
            <div style={{ marginBottom: '16px' }}>
              <Input
                placeholder="Search trees by name, description, or company..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </div>
            <Space style={{ marginBottom: '16px', fontSize: '12px' }}>
              <Text type="secondary">
                Showing {filteredTrees.length} of {totalTrees} trees
              </Text>
              <Text type="secondary">•</Text>
              <Text type="secondary">
                Total artifacts: <Badge count={totalArtifacts} showZero style={{ backgroundColor: '#1890ff' }} />
              </Text>
            </Space>
            <Table
              columns={columns}
              dataSource={filteredTrees}
              rowKey="id"
              loading={loading}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} trees`,
              }}
              scroll={{ y: 'calc(100vh - 400px)' }}
              onRow={(record) => ({
                onClick: () => handleSelectTree(record),
                style: {
                  cursor: 'pointer',
                  backgroundColor: selectedTree?.id === record.id ? '#e6f7ff' : 'transparent'
                }
              })}
              rowClassName={(record) =>
                selectedTree?.id === record.id ? 'selected-tree-row' : ''
              }
            />
          </Card>
        </Col>

        {/* Right Pane - Tree Details */}
        <Col xs={24} lg={14} xl={15} className="artifact-tree-details-panel">
          <Card
            title={
              <Title level={4} style={{ margin: 0 }}>
                {isCreating ? 'Create Artifact Tree' : selectedTree ? `Edit: ${selectedTree.name}` : 'Tree Details'}
              </Title>
            }
            className="tree-details-card"
          >
            {selectedTree || isCreating ? (
              <Form 
                form={form} 
                layout="vertical"
                onFinish={handleSaveTree}
              >
                <Form.Item
                  name="name"
                  label="Tree Name"
                  rules={[{ required: true, message: 'Please enter tree name' }]}
                >
                  <Input placeholder="Enter tree name" />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="Description"
                >
                  <TextArea rows={4} placeholder="Enter tree description" />
                </Form.Item>
                <Form.Item
                  name="company_id"
                  label="Company"
                  tooltip="Optional: Associate this tree with a company"
                >
                  <Select
                    placeholder="Select a company (optional)"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    loading={loadingCompanies}
                    filterOption={(input, option) => {
                      const children = option?.children;
                      const text = typeof children === 'string' ? children : String(children || '');
                      return text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                    }}
                  >
                    {companies.map((company) => (
                      <Option key={company.id} value={company.id}>
                        {company.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button type="primary" htmlType="submit">
                      {isCreating ? 'Create Tree' : 'Save Changes'}
                    </Button>
                    {selectedTree && !isCreating && (
                      <Popconfirm
                        title="Are you sure you want to delete this artifact tree?"
                        description="This will soft-delete the tree. This action cannot be undone."
                        onConfirm={() => handleDeleteTree(selectedTree.id)}
                        okText="Yes"
                        cancelText="No"
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          Delete Tree
                        </Button>
                      </Popconfirm>
                    )}
                    <Button onClick={() => {
                      setSelectedTree(null);
                      setIsCreating(false);
                      form.resetFields();
                    }}>
                      Cancel
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Text type="secondary">
                  Select a tree from the list to view and edit its details, or click "Add Tree" to create a new one.
                </Text>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

